import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'

import styled, { css } from 'styled-components'

import { SportCompetitionScheduleApiProps } from '@hooks/useSportEntitySchedule/types'
import useSportCompetitionSchedule from '@hooks/useSportEntitySchedule/useSportCompetitionSchedule'
import Tile from '@components/RelatedMatches/Tile'

import Dropdown from './Dropdown'
import SportEntitySchedulePlaceholder from './SportEntityPlaceholder'
import { getCurrentRoundId } from './utils'

const OuterWrapper = styled.div`
  ${({
    theme: {
      spacing: { spacing24 },
    },
  }) => css`
    display: flex;
    flex-direction: column;
    gap: ${spacing24};
    align-items: center;
  `}
`

const SportEntityScheduleWrapper = styled.div`
  ${({
    theme: {
      spacing: { spacing16 },
    },
  }) => css`
    display: flex;
    flex-direction: column;
    gap: ${spacing16};
    width: 100%;
  `}
`
const SportEntityScheduleGameWrapper = styled.div`
  width: 100%;
  > * {
    width: 100%;
  }
`

const SportCompetitionSchedule: FunctionComponent<
  SportCompetitionScheduleApiProps
> = ({ seasonId, typeOfSport }) => {
  const { data, isSuccess, error } = useSportCompetitionSchedule({
    entityId: seasonId,
    typeOfSport,
  })

  const [selectedRoundId, setSelectedRoundId] = useState('')

  const { rounds, items } = data ?? { rounds: [], items: {} }

  const currentRoundId = useMemo(() => {
    if (items && rounds) {
      return getCurrentRoundId({ rounds, items })
    }
  }, [items, rounds])

  const isDataEmpty =
    isSuccess &&
    (rounds?.length === 0 ||
      Object.values(items).every((roundItems) => roundItems.length === 0))

  useEffect(() => {
    if (!selectedRoundId && currentRoundId) {
      setSelectedRoundId(currentRoundId)
    }
  }, [currentRoundId, selectedRoundId])

  const matches = useMemo(
    () => items?.[selectedRoundId] ?? [],
    [items, selectedRoundId],
  )

  const currentRoundMatches = useMemo(
    () =>
      matches.map((item) => (
        <SportEntityScheduleGameWrapper
          key={`${item.typeOfSport}-${item.matchId}`}
        >
          <Tile
            matchId={item.matchId}
            typeOfSport={item.typeOfSport}
            link={item.link}
          />
        </SportEntityScheduleGameWrapper>
      )),
    [matches],
  )

  const onSelectHandler = useCallback((currentRoundId: string) => {
    setSelectedRoundId(currentRoundId)
  }, [])

  return (
    <>
      <SportEntitySchedulePlaceholder error={error} isDataEmpty={isDataEmpty} />
      <Dropdown
        rounds={rounds}
        selectedRoundId={selectedRoundId}
        onSelect={onSelectHandler}
      />
      <OuterWrapper>
        <SportEntityScheduleWrapper>
          {currentRoundMatches}
        </SportEntityScheduleWrapper>
      </OuterWrapper>
    </>
  )
}

export default SportCompetitionSchedule
